import React, { useState, useEffect, useContext, useMemo } from "react";
import { Table, Form, Button, Badge, Modal, Spinner, Row, Col } from "react-bootstrap";
import { DarkModeContext } from "../../context/DarkModeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faRotateRight, faUser, faEnvelope, faPhone, faBuilding } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const ESTADOS = [
    { value: "nuevo", label: "Nuevo", bg: "primary" },
    { value: "contactado", label: "Contactado", bg: "warning" },
    { value: "en_seguimiento", label: "En seguimiento", bg: "info" },
    { value: "cerrado", label: "Cerrado", bg: "success" },
    { value: "sin_interes", label: "Sin interés", bg: "secondary" },
];

const badgeColor = (v) => ESTADOS.find((e) => e.value === v)?.bg ?? "light";
const estadoLabel = (v) => ESTADOS.find((e) => e.value === v)?.label ?? v ?? "-";
const tituloLead = (lead) => (lead?.nombre && lead.nombre !== "-" ? lead.nombre : (lead?.leadgenId || "-"));
const buildMetaErrorMessage = (payload = {}, fallbackStatus = "") => {
    const base = payload?.error || fallbackStatus || "No se pudo conectar con Meta";
    if (!Array.isArray(payload?.details) || payload.details.length === 0) {
        return base;
    }

    const detail = payload.details
        .map((item) => {
            const page = item?.pageName || item?.pageId || "Pagina";
            const reason = item?.message || item?.code || "Sin detalle";
            return `${page}: ${reason}`;
        })
        .join(" | ");

    return `${base}. ${detail}`;
};

const TablaLeadsMeta = () => {
    const { darkMode } = useContext(DarkModeContext);

    const [datos, setDatos] = useState([]);
    const [campanasPorId, setCampanasPorId] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [paginaActual, setPaginaActual] = useState(1);
    const registrosPorPagina = 15;

    const [filtroTexto, setFiltroTexto] = useState("");
    const [filtroEstado, setFiltroEstado] = useState("todos");
    const [filtroFormulario, setFiltroFormulario] = useState("todos");
    const [filtroCampana, setFiltroCampana] = useState("todos");

    const [showModal, setShowModal] = useState(false);
    const [leadSel, setLeadSel] = useState(null);
    const [nuevoEstado, setNuevoEstado] = useState("nuevo");
    const [observaciones, setObservaciones] = useState("");

    const cargarLeads = async () => {
        setLoading(true);
        try {
            const r = await fetch("/api/meta/leads/facebook");
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(buildMetaErrorMessage(err, `Error ${r.status}`));
            }
            setDatos(await r.json());
        } catch (err) {
            Swal.fire("Error al cargar leads", err.message || "No se pudo conectar con Meta", "error");
        } finally {
            setLoading(false);
        }
    };

    const cargarCampanasMeta = async () => {
        try {
            const r = await fetch("/api/meta/campanas/meta");
            if (!r.ok) return;
            const data = await r.json();
            if (!Array.isArray(data)) return;
            const mapa = {};
            data.forEach((c) => {
                if (!c?.id) return;
                mapa[c.id] = c.name || c.id;
            });
            setCampanasPorId(mapa);
        } catch {
            // fallback a id si no hay catálogo
        }
    };

    useEffect(() => {
        cargarLeads();
        cargarCampanasMeta();
    }, []);

    const formularios = useMemo(() => [...new Map(datos.filter((d) => d.formId).map((d) => [d.formId, d.formName || d.formId])).entries()], [datos]);
    const campanas = useMemo(() => {
        const mapa = new Map();
        datos.forEach((d) => {
            if (!d.campaignId) return;
            const nombre = d.campaignName || campanasPorId[d.campaignId] || d.campaignId;
            if (!mapa.has(d.campaignId)) mapa.set(d.campaignId, nombre);
        });
        return [...mapa.entries()];
    }, [datos, campanasPorId]);

    const datosFiltrados = useMemo(() => {
        const texto = filtroTexto.toLowerCase();
        return datos.filter((d) => {
            const matchTexto =
                (d.nombre || "").toLowerCase().includes(texto) ||
                (d.email || "").toLowerCase().includes(texto) ||
                (d.telefono || "").toLowerCase().includes(texto) ||
                (d.empresa || "").toLowerCase().includes(texto);
            const matchEstado = filtroEstado === "todos" || d.estado === filtroEstado;
            const matchFormulario = filtroFormulario === "todos" || d.formId === filtroFormulario;
            const matchCampana = filtroCampana === "todos" || d.campaignId === filtroCampana;
            return matchTexto && matchEstado && matchFormulario && matchCampana;
        });
    }, [datos, filtroTexto, filtroEstado, filtroFormulario, filtroCampana]);

    const totalPaginas = Math.ceil(datosFiltrados.length / registrosPorPagina);
    const inicio = (paginaActual - 1) * registrosPorPagina;
    const paginados = datosFiltrados.slice(inicio, inicio + registrosPorPagina);

    const abrirModal = (lead) => {
        setLeadSel(lead);
        setNuevoEstado(lead.estado || "nuevo");
        setObservaciones(lead.observaciones || "");
        setShowModal(true);
    };

    const guardarEstado = async () => {
        if (!leadSel?.dbId) return;
        setSaving(true);
        try {
            const r = await fetch(`/api/meta/leads/${leadSel.dbId}/estado`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ estado: nuevoEstado, observaciones }),
            });
            if (!r.ok) throw new Error(`Error ${r.status}`);

            setDatos((prev) => prev.map((l) =>
                l.dbId === leadSel.dbId ? { ...l, estado: nuevoEstado, observaciones } : l
            ));
            setShowModal(false);
            Swal.fire({ title: "Guardado", icon: "success", timer: 1200, showConfirmButton: false });
        } catch {
            Swal.fire("Error", "No se pudo actualizar el estado", "error");
        } finally {
            setSaving(false);
        }
    };

    const exportarExcel = () => {
        const ws = XLSX.utils.json_to_sheet(datosFiltrados.map((d) => ({
            Fecha: d.createdTime ? new Date(d.createdTime).toLocaleDateString("es-AR") : "-",
            Nombre: d.nombre || "-",
            Email: d.email || "-",
            Telefono: d.telefono || "-",
            Empresa: d.empresa || "-",
            Estado: estadoLabel(d.estado),
            Formulario: d.formName || "-",
            Campana: d.campaignName || campanasPorId[d.campaignId] || d.campaignId || "-",
            Pagina: d.pageName || "-",
            Observaciones: d.observaciones || "",
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Leads Meta");
        XLSX.writeFile(wb, "leads_meta.xlsx");
    };

    const resetFiltros = () => {
        setFiltroTexto("");
        setFiltroEstado("todos");
        setFiltroFormulario("todos");
        setFiltroCampana("todos");
        setPaginaActual(1);
    };

    const thStyle = { backgroundColor: darkMode ? "#2c2c2c" : "#f8f9fa", color: darkMode ? "#fff" : "#000", fontSize: 13, whiteSpace: "nowrap" };
    const tdStyle = { fontSize: 13, verticalAlign: "middle" };

    return (
        <div className={darkMode ? "text-white" : ""}>
            <Row className="g-2 mb-3 align-items-end">
                <Col xs={12} md={4}>
                    <Form.Control
                        size="sm"
                        placeholder="Buscar nombre, email, teléfono, empresa..."
                        value={filtroTexto}
                        onChange={(e) => { setFiltroTexto(e.target.value); setPaginaActual(1); }}
                    />
                </Col>
                <Col xs={6} md={2}>
                    <Form.Select size="sm" value={filtroEstado} onChange={(e) => { setFiltroEstado(e.target.value); setPaginaActual(1); }}>
                        <option value="todos">Todos los estados</option>
                        {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                    </Form.Select>
                </Col>
                <Col xs={6} md={2}>
                    <Form.Select size="sm" value={filtroFormulario} onChange={(e) => { setFiltroFormulario(e.target.value); setPaginaActual(1); }}>
                        <option value="todos">Todos los formularios</option>
                        {formularios.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                    </Form.Select>
                </Col>
                <Col xs={6} md={2}>
                    <Form.Select size="sm" value={filtroCampana} onChange={(e) => { setFiltroCampana(e.target.value); setPaginaActual(1); }}>
                        <option value="todos">Todas las campañas</option>
                        {campanas.map(([id, nombre]) => (<option key={id} value={id}>{nombre}{nombre !== id ? ` (${id})` : ""}</option>))}
                    </Form.Select>
                </Col>
                <Col xs="auto" className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={resetFiltros} title="Limpiar filtros">Limpiar</Button>
                    <Button size="sm" variant="outline-success" onClick={exportarExcel}><FontAwesomeIcon icon={faFileExcel} /> Excel</Button>
                    <Button size="sm" variant="outline-secondary" onClick={cargarLeads} disabled={loading}><FontAwesomeIcon icon={faRotateRight} /></Button>
                    <span className="text-muted align-self-center" style={{ fontSize: 12 }}>{datosFiltrados.length} leads</span>
                </Col>
            </Row>

            <div className="d-flex gap-2 flex-wrap mb-3">
                {ESTADOS.map((e) => {
                    const count = datos.filter((d) => d.estado === e.value).length;
                    if (!count) return null;
                    return (
                        <Badge
                            key={e.value}
                            bg={e.bg}
                            style={{ cursor: "pointer", fontSize: 12, padding: "5px 10px" }}
                            onClick={() => { setFiltroEstado(filtroEstado === e.value ? "todos" : e.value); setPaginaActual(1); }}
                        >
                            {e.label}: {count}
                        </Badge>
                    );
                })}
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
                <>
                    <Table bordered hover responsive size="sm" variant={darkMode ? "dark" : ""}>
                        <thead>
                            <tr>
                                <th style={thStyle}>#</th>
                                <th style={thStyle}>Fecha</th>
                                <th style={thStyle}>Nombre</th>
                                <th style={thStyle}>Email</th>
                                <th style={thStyle}>Teléfono</th>
                                <th style={thStyle}>Empresa</th>
                                <th style={thStyle}>Formulario</th>
                                <th style={thStyle}>Estado</th>
                                <th style={thStyle}>Observaciones</th>
                                <th style={thStyle}>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginados.length === 0 ? (
                                <tr><td colSpan={10} className="text-center text-muted py-4">Sin leads para los filtros seleccionados</td></tr>
                            ) : paginados.map((lead, i) => (
                                <tr key={lead.leadgenId}>
                                    <td style={{ ...tdStyle, color: darkMode ? "#888" : "#aaa", fontSize: 11 }}>{inicio + i + 1}</td>
                                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                                        {lead.createdTime ? new Date(lead.createdTime).toLocaleDateString("es-AR") : "-"}
                                    </td>
                                    <td style={tdStyle}><strong>{lead.nombre || "-"}</strong></td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>{lead.email || "-"}</td>
                                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{lead.telefono || "-"}</td>
                                    <td style={tdStyle}>{lead.empresa || "-"}</td>
                                    <td style={{ ...tdStyle, fontSize: 11, color: darkMode ? "#aaa" : "#666", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {lead.formName || lead.formId || "-"}
                                    </td>
                                    <td style={tdStyle}>
                                        <Badge bg={badgeColor(lead.estado)}>{estadoLabel(lead.estado)}</Badge>
                                    </td>
                                    <td style={{ ...tdStyle, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: darkMode ? "#ccc" : "#555", fontSize: 12 }}>
                                        {lead.observaciones || <span className="text-muted">—</span>}
                                    </td>
                                    <td style={tdStyle}>
                                        <Button size="sm" variant="outline-primary" onClick={() => abrirModal(lead)}>
                                            Gestionar
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {totalPaginas > 1 && (
                        <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
                            <Button size="sm" variant="outline-secondary" disabled={paginaActual === 1} onClick={() => setPaginaActual((p) => p - 1)}>‹ Anterior</Button>
                            <span style={{ fontSize: 13 }}>{paginaActual} / {totalPaginas}</span>
                            <Button size="sm" variant="outline-secondary" disabled={paginaActual === totalPaginas} onClick={() => setPaginaActual((p) => p + 1)}>Siguiente ›</Button>
                        </div>
                    )}
                </>
            )}

            <Modal show={showModal} onHide={() => !saving && setShowModal(false)} centered size="lg">
                <Modal.Header closeButton className={darkMode ? "bg-dark text-white border-secondary" : ""}>
                    <Modal.Title style={{ fontSize: 16 }}>
                        Gestionar lead — <strong>{tituloLead(leadSel)}</strong>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className={darkMode ? "bg-dark text-white" : ""}>
                    <div className={`rounded p-3 mb-3 ${darkMode ? "bg-secondary bg-opacity-25" : "bg-light"}`} style={{ fontSize: 13 }}>
                        <Row className="g-2">
                            <Col xs={12} md={6}>
                                <div><FontAwesomeIcon icon={faUser} className="me-2 text-muted" /><strong>Nombre:</strong> {leadSel?.nombre || "—"}</div>
                                <div className="mt-1"><FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" /><strong>Email:</strong> {leadSel?.email || "—"}</div>
                                <div className="mt-1"><FontAwesomeIcon icon={faPhone} className="me-2 text-muted" /><strong>Teléfono:</strong> {leadSel?.telefono || "—"}</div>
                                <div className="mt-1"><FontAwesomeIcon icon={faBuilding} className="me-2 text-muted" /><strong>Empresa:</strong> {leadSel?.empresa || "—"}</div>
                            </Col>
                            <Col xs={12} md={6}>
                                <div><strong>Página:</strong> {leadSel?.pageName || "—"}</div>
                                <div className="mt-1"><strong>Formulario:</strong> {leadSel?.formName || leadSel?.formId || "—"}</div>
                                <div className="mt-1"><strong>Campaña:</strong> {leadSel?.campaignName || campanasPorId[leadSel?.campaignId] || leadSel?.campaignId || "—"}</div>
                                <div className="mt-1"><strong>Campaña ID:</strong> <code style={{ fontSize: 11 }}>{leadSel?.campaignId || "—"}</code></div>
                                <div className="mt-1"><strong>Recibido:</strong> {leadSel?.createdTime ? new Date(leadSel.createdTime).toLocaleString("es-AR") : "—"}</div>
                            </Col>
                        </Row>
                        {leadSel?.campos && Object.keys(leadSel.campos).length > 0 && (
                            <div className="mt-2 pt-2 border-top" style={{ fontSize: 12 }}>
                                <strong>Datos del formulario:</strong>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    {Object.entries(leadSel.campos).map(([k, v]) => (
                                        <span key={k} className={`badge ${darkMode ? "bg-secondary" : "bg-light text-dark border"}`} style={{ fontWeight: 400 }}>
                                            <span className="text-muted">{k}:</span> {v}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <Row className="g-3">
                        <Col xs={12} md={4}>
                            <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>Estado</Form.Label>
                            <Form.Select
                                size="sm"
                                value={nuevoEstado}
                                onChange={(e) => setNuevoEstado(e.target.value)}
                                className={darkMode ? "bg-dark text-white border-secondary" : ""}
                            >
                                {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={8}>
                            <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>Observaciones</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                size="sm"
                                value={observaciones}
                                onChange={(e) => setObservaciones(e.target.value)}
                                placeholder="Notas sobre el lead, resultado del contacto..."
                                className={darkMode ? "bg-dark text-white border-secondary" : ""}
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className={darkMode ? "bg-dark border-secondary" : ""}>
                    <Button variant="secondary" size="sm" onClick={() => setShowModal(false)} disabled={saving}>Cancelar</Button>
                    <Button variant="primary" size="sm" onClick={guardarEstado} disabled={saving}>
                        {saving ? <><Spinner size="sm" animation="border" className="me-1" />Guardando...</> : "Guardar cambios"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TablaLeadsMeta;
